import React, { useState, useMemo } from 'react';

import { EventType } from 'types/event.types';
import { useSessionsQuery } from '@event-editor/hooks/useSessionsQuery';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';

import {
  GuestsEmptyState,
  GuestsList,
  GuestsSummary,
  GuestsDraftState,
  GuestCheckInBanners,
  GuestsSectionHeader,
  GuestsSessionSelector,
  CheckInLinkButton
} from '../components/GuestsSection';

const GuestsSection: React.FC = () => {
  const event = useEditableEvent();
  const [currentSessionId, setCurrentSessionId] = useState<number | null>(null);

  const isMultiSessions = event.eventType === EventType.MULTI_SESSIONS;

  const { data: eventSessions } = useSessionsQuery(event, {
    enabled: !!event.id && event.isPublished && isMultiSessions,
    select: (data) => data.filter((session) => session.isPublished)
  });

  const selectedSession = useMemo(() => {
    if (!currentSessionId && eventSessions && eventSessions.length > 0) {
      return eventSessions[0];
    }
    return eventSessions?.find((session) => Number(session.id) === currentSessionId);
  }, [currentSessionId, eventSessions]);

  if (!event.isPublished) {
    return <GuestsDraftState />;
  }

  if ((event.totalGuests ?? 0) === 0) {
    if (event.hasEnded) {
      return (
        <GuestsEmptyState
          description="Check in guests that attended the event"
          action={
            !event.isThirtyDaysAfterEventEnded ? (
              <CheckInLinkButton eventId={event.id!} sessionId={selectedSession?.id?.toString()} />
            ) : undefined
          }
        />
      );
    }
    return <GuestsEmptyState />;
  }

  // Published and has guests
  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <GuestCheckInBanners eventSessions={eventSessions} />

      <GuestsSectionHeader session={selectedSession} />

      {isMultiSessions && (
        <div className="tw-mt-4">
          <GuestsSessionSelector sessions={eventSessions ?? []} onChange={setCurrentSessionId} />
        </div>
      )}

      <div className="tw-mb-10 tw-mt-7">
        <GuestsSummary session={selectedSession} />
      </div>

      <GuestsList session={selectedSession} />
    </div>
  );
};

export { GuestsSection };
